import { Seo } from "@/components/seo/Seo";
import { staticSeoRoutes } from "@/config/seo.config";

const meta = staticSeoRoutes.find((route) => route.path === "/terms")!;

export function TermsPage() {
  return (
    <>
      <Seo
        path={meta.path}
        title={meta.title}
        description={meta.description}
        robots={meta.robots}
      />
      <section className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        <h1 className="text-4xl font-semibold">Terms of Use</h1>
        <p className="mt-3 text-sm text-neutral-500 dark:text-neutral-400">
          Last updated: June 2026
        </p>

        <div className="mt-8 space-y-8 leading-7 text-neutral-700 dark:text-neutral-300">
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
              1. Acceptance of Terms
            </h2>
            <p>
              By accessing or using VideoTranscriptSaver ("the Service", "we",
              "our"), you agree to be bound by these Terms of Use ("Terms"). If
              you do not agree to all of these Terms, do not use the Service. We
              reserve the right to modify these Terms at any time. Continued use
              of the Service after changes are posted constitutes your
              acceptance of the revised Terms.
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
              2. Eligibility
            </h2>
            <p>
              You must be at least 13 years of age to use the Service. By using
              the Service, you represent and warrant that you meet this age
              requirement. If you are using the Service on behalf of an
              organisation, you represent that you have authority to bind that
              organisation to these Terms.
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
              3. Permitted Use and Your Responsibilities
            </h2>
            <p>
              You may only submit media — including uploaded files, browser
              recordings, and direct media links — that you own, created
              yourself, hold a valid licence for, or are otherwise legally
              permitted to access and process. You are solely responsible for
              ensuring you have all necessary rights, permissions, and
              clearances for any content you submit to the Service.
            </p>
            <p>
              You agree not to use the Service to process content that: (a)
              infringes any third-party intellectual property, privacy, or
              publicity rights; (b) violates any applicable law or regulation;
              (c) is unlawful, defamatory, obscene, or harmful; or (d)
              constitutes unauthorised surveillance or recording of individuals
              without their consent.
            </p>
            <p>
              You agree not to attempt to circumvent rate limits,
              reverse-engineer the Service, submit automated bulk requests in a
              manner that degrades service quality for others, or otherwise
              abuse the platform.
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
              4. Accuracy of AI-Generated Output
            </h2>
            <p>
              Transcripts, captions, summaries, notes, action items, and
              chapters produced by the Service are generated by AI and are
              provided as helpful drafts only. They may contain errors,
              omissions, or inaccuracies. You must independently verify all
              AI-generated output against the original recording before relying
              on it for any purpose. The Service does not constitute legal,
              medical, financial, professional, or any other form of regulated
              advice. We disclaim all liability for decisions made in reliance
              on AI-generated output without independent verification.
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
              5. Intellectual Property
            </h2>
            <p>
              You retain all ownership rights in content you submit. You grant
              us a limited, transient licence to process your content solely to
              deliver the Service to you. We claim no ownership over content you
              submit or outputs generated from it.
            </p>
            <p>
              The Service itself, including its interface, branding, and
              underlying technology, is owned by us and is protected by
              applicable intellectual property laws. You may not copy, modify,
              distribute, reverse engineer, or create derivative works from any
              part of the Service without our prior written consent.
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
              6. Disclaimer of Warranties
            </h2>
            <p className="font-medium">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
              WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT
              LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
              PURPOSE, NON-INFRINGEMENT, ACCURACY, RELIABILITY, OR UNINTERRUPTED
              AVAILABILITY. WE DO NOT WARRANT THAT THE SERVICE WILL BE
              ERROR-FREE, THAT DEFECTS WILL BE CORRECTED, OR THAT THE SERVICE OR
              ITS SERVERS ARE FREE OF VIRUSES OR HARMFUL COMPONENTS. YOUR USE OF
              THE SERVICE IS ENTIRELY AT YOUR OWN RISK.
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
              7. Limitation of Liability
            </h2>
            <p className="font-medium">
              TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, WE AND OUR
              AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AND LICENSORS
              SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
              CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES — INCLUDING LOSS OF
              PROFITS, DATA, GOODWILL, OR BUSINESS OPPORTUNITIES — ARISING OUT
              OF OR IN CONNECTION WITH YOUR USE OF OR INABILITY TO USE THE
              SERVICE, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH
              DAMAGES.
            </p>
            <p className="font-medium">
              IN NO EVENT SHALL OUR TOTAL AGGREGATE LIABILITY TO YOU FOR ALL
              CLAIMS ARISING OUT OF OR RELATING TO THE SERVICE EXCEED THE
              GREATER OF (A) THE AMOUNT YOU PAID US IN THE TWELVE MONTHS
              PRECEDING THE CLAIM OR (B) USD $10. SOME JURISDICTIONS DO NOT
              ALLOW THE EXCLUSION OR LIMITATION OF CERTAIN DAMAGES, SO SOME OF
              THE ABOVE MAY NOT APPLY TO YOU.
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
              8. Indemnification
            </h2>
            <p>
              You agree to indemnify, defend, and hold harmless
              VideoTranscriptSaver and its affiliates, officers, directors,
              employees, and agents from and against any and all claims,
              damages, liabilities, losses, costs, and expenses (including
              reasonable legal fees) arising out of or related to: (a) your use
              of the Service; (b) your violation of these Terms; (c) your
              submission of content you did not have the right to process; (d)
              your infringement of any third-party intellectual property,
              privacy, or other rights; or (e) any claim that your content
              caused damage to a third party.
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
              9. Third-Party Services and Links
            </h2>
            <p>
              The Service relies on third-party infrastructure and AI providers
              to function. We are not responsible for the availability,
              accuracy, or terms of those third-party services. Any links to
              external websites are provided for convenience only; we do not
              endorse and are not responsible for the content of external sites.
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
              10. Service Modifications and Termination
            </h2>
            <p>
              We reserve the right to modify, suspend, or discontinue the
              Service — or any part of it — at any time, with or without notice,
              and without liability to you. We may also apply usage limits or
              rate limits at any time to maintain service quality. The free tier
              is provided at our discretion and may change.
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
              11. Waitlist and Premium Tier
            </h2>
            <p>
              Joining the waitlist for a future premium tier does not create a
              contract, guarantee access, establish any pricing commitment, or
              imply any specific launch timeline or feature set. We reserve the
              right to alter, delay, or cancel any planned premium offering
              without obligation.
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
              12. Copyright and DMCA
            </h2>
            <p>
              We respect intellectual property rights and expect users to do the
              same. If you believe content accessible through or submitted to
              the Service infringes your copyright, please submit a written
              notice to us with: your contact information; identification of the
              copyrighted work claimed to be infringed; a statement of your
              good-faith belief that the use is unauthorised; and a statement,
              under penalty of perjury, that the information in the notice is
              accurate and that you are the copyright owner or authorised to act
              on their behalf. We will respond to valid notices in accordance
              with applicable law.
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
              13. Governing Law and Disputes
            </h2>
            <p>
              These Terms are governed by and construed in accordance with
              applicable law, without regard to conflict-of-law principles. Any
              dispute arising out of or relating to these Terms or the Service
              shall first be attempted to be resolved through good-faith
              negotiation. If not resolved within 30 days, disputes shall be
              submitted to binding individual arbitration under the rules of a
              mutually agreed arbitration body, or, where arbitration is not
              available under applicable law, to a court of competent
              jurisdiction. You waive any right to participate in a class-action
              lawsuit or class-wide arbitration against us.
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
              14. Severability
            </h2>
            <p>
              If any provision of these Terms is found to be unenforceable or
              invalid, that provision shall be limited or eliminated to the
              minimum extent necessary so that the remaining Terms shall
              continue in full force and effect.
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
              15. Entire Agreement
            </h2>
            <p>
              These Terms, together with our Privacy Policy, constitute the
              entire agreement between you and VideoTranscriptSaver with respect
              to the Service and supersede all prior or contemporaneous
              communications, whether electronic, oral, or written, between you
              and us regarding the Service.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
